import { useCallback, useEffect, useState } from 'react';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import t from '../../../localization/en/translation.json';
import './gridHeaderToolbar.scss';
import { MenuItem, Popover } from '@material-ui/core';
import { AppIcon, AppTooltip } from '../../../theme';
import { v4 } from 'uuid';
import {
  angleDown,
  arrowDownToBracket,
  arrowUpFromBracket,
  circlePlus,
  floppyDisk,
  globe,
  trashCan,
  userCheck,
} from '../../../theme/icons';
import { useDispatch, useSelector } from 'react-redux';
import { APP_STATE } from '../../../store/reducers';
import { getProjectUsersListSuccess } from '../../../store/actions/project-actions';
import { newUserRowData } from '../listUsers/listUserConstants';
import DeleteUsersConfirmModal from '../deleteConfirmModal/deleteConfirmModal';
import {
  getAtleastOneAdminUser,
  getAtleastOneOldUser,
  getNewUserLimit,
} from '../../../utils/manageUserHelpers';
import { showActionToast } from '../../../store/actions/notification-actions';
import {
  addNewUserSelected,
  getUserLanguage,
} from '../../../store/actions/manage-user-actions';
import { getCountriesCodeAction } from '../../../store/actions/data-locations-actions';

interface IGridHeaderToolbarProps {
  isProjectAdminUser?: boolean;
  isUserProjectAdmin?: boolean;
  onRemoveUserClick: Function;
  updatedUsersList: Array<any>;
  gridUpdatedUserRoles: Array<any>;
  onDomainClick?: Function;
  onExportUserClick?: Function;
  onExportTemplateClick?: Function;
  isProvisioning?: boolean;
  onSaveClick: () => void;
  projectId: string;
  removeNewUserFromGrid: Function;
  onBulkRoleAssignClick?: Function;
  clearSearchFilter: Function;
  onImportClick: Function
}

const GridHeaderToolbar: React.FC<IGridHeaderToolbarProps> = ({
  isProjectAdminUser,
  isUserProjectAdmin,
  onRemoveUserClick,
  updatedUsersList,
  gridUpdatedUserRoles,
  onDomainClick,
  onExportUserClick,
  onExportTemplateClick,
  isProvisioning = true,
  onSaveClick,
  projectId,
  removeNewUserFromGrid,
  onBulkRoleAssignClick,
  clearSearchFilter,
  onImportClick
}) => {
  const dispatch = useDispatch();
  const [isOnlyIcons, setIsOnlyIcons] = useState<boolean>(false);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);
  const { users, userGridPagination, userAccess } = useSelector(
    (state: APP_STATE) => state.projects
  );
  const { roles } = useSelector((state: APP_STATE) => state.meta);
  const manageUserData = useSelector(
    (state: APP_STATE) => state.manageUserReducer
  );

  useEffect(() => {
    dispatch(getUserLanguage());
    dispatch(getCountriesCodeAction());
  }, [dispatch]);

  const handleExportClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDomainClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onDomainClick && onDomainClick();
  };

  const handleBulkRoleAssignClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    onBulkRoleAssignClick && onBulkRoleAssignClick();
  };

   const handleImportClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onImportClick && onImportClick();
  };

  const onAddUserClick = () => {
    clearSearchFilter();
    if (getNewUserLimit(users)) {
      dispatch(
        getProjectUsersListSuccess({
          data: {
            records: [{ ...newUserRowData, id: v4() }, ...users],
            pagination: userGridPagination,
          },
        })
      );
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleExportUserClick = () => {
    onExportUserClick && onExportUserClick();
    handleClose();
  };

  const handleExportTemplateClick = () => {
    onExportTemplateClick && onExportTemplateClick();
    handleClose();
  };

  const checkUserDelete = () => {
    if (
      userAccess?.length > 1 &&
      getAtleastOneAdminUser(userAccess, updatedUsersList, roles)
    ) {
      dispatch(
        showActionToast({
          message: t.app_crash_msg,
          type: 'error',
        })
      );
      return;
    }
    if (
      updatedUsersList?.length &&
      (isUserProjectAdmin || isProjectAdminUser)
    ) {
      setIsDeleteConfirm(true);
    }
  };

  const handleDeleteUser = () => {
    const { isAllNewUser, updatedusers, updatedNewUser } = getAtleastOneOldUser(
      updatedUsersList,
      users,
      manageUserData.addNewUserSelected
    );
    if (isAllNewUser) {
      dispatch(addNewUserSelected(updatedNewUser));
      removeNewUserFromGrid(updatedusers);
    } else {
      onRemoveUserClick();
    }
    setIsDeleteConfirm(false);
  };

  const isBulkRoleDisabled = useCallback(() => {
    return !updatedUsersList.filter((user) => !user.userDetails.isNewAddedRow)
      .length;
  }, [updatedUsersList]);

  return (
    <>
      <div className="grid-header-toolbar">
        <div className="toolbar-left">
          <div className={`toolbar-group ${isOnlyIcons && 'expanded'}`}>
            <div
              className={`toolbar-item ${
                !gridUpdatedUserRoles?.length && 'disabled'
              }`}
              onClick={() => onSaveClick()}
            >
              {isOnlyIcons ? (
                <AppTooltip
                  placement={'top'}
                  className="roles-selector"
                  title={t.manage_user_toolbar_save}
                >
                  <AppIcon icon={floppyDisk} />
                </AppTooltip>
              ) : (
                <>
                  <AppIcon icon={floppyDisk} />
                  <span>{t.manage_user_toolbar_save}</span>
                </>
              )}
            </div>
            {(isUserProjectAdmin || isProjectAdminUser) && (
              <div
                className={`toolbar-item ${
                  users?.length && getNewUserLimit(users) ? '' : 'disabled'
                }`}
                onClick={onAddUserClick}
              >
                {isOnlyIcons ? (
                  <AppTooltip
                    placement={'top'}
                    className="roles-selector"
                    title={t.manage_user_toolbar_addnew}
                  >
                    <AppIcon icon={circlePlus} />
                  </AppTooltip>
                ) : (
                  <>
                    <AppIcon icon={circlePlus} />
                    <span>{t.manage_user_toolbar_addnew}</span>
                  </>
                )}
              </div>
            )}
            <div
              className={`toolbar-item ${
                !updatedUsersList.length && 'disabled'
              }`}
              onClick={checkUserDelete}
            >
              {isOnlyIcons ? (
                <AppTooltip
                  placement={'top'}
                  className="roles-selector"
                  title={t.manage_user_toolbar_remove}
                >
                  <AppIcon icon={trashCan} />
                </AppTooltip>
              ) : (
                <>
                  <AppIcon icon={trashCan} />
                  <span>{t.manage_user_toolbar_remove}</span>
                </>
              )}
            </div>
            <div
              className={`toolbar-item ${isBulkRoleDisabled() && 'disabled'}`}
              onClick={handleBulkRoleAssignClick}
            >
              {isOnlyIcons ? (
                <AppTooltip
                  placement={'top'}
                  className="roles-selector"
                  title={t.manage_user_toolbar_role}
                >
                  <AppIcon icon={userCheck} />
                </AppTooltip>
              ) : (
                <>
                  <AppIcon icon={userCheck} />
                  <span>{t.manage_user_toolbar_role}</span>
                </>
              )}
            </div>
          </div>
          <div className={`toolbar-group ${isOnlyIcons && 'expanded'}`}>
            <div className="toolbar-item" onClick={handleImportClick}>
              {isOnlyIcons ? (
                <AppTooltip
                  placement={'top'}
                  className="roles-selector"
                  title={t.manage_user_toolbar_import}
                >
                  <AppIcon icon={arrowDownToBracket} />
                </AppTooltip>
              ) : (
                <>
                  <AppIcon icon={arrowDownToBracket} />
                  <span>{t.manage_user_toolbar_import}</span>
                </>
              )}
            </div>
            <div
              onClick={(e) => handleExportClick(e)}
              className="toolbar-item export-icon"
            >
              {isOnlyIcons ? (
                <AppTooltip
                  placement={'top'}
                  className="roles-selector"
                  title={t.manage_user_toolbar_export}
                >
                  <div>
                    <AppIcon icon={arrowUpFromBracket} className="m-r-5" />
                    <AppIcon icon={angleDown} />
                  </div>
                </AppTooltip>
              ) : (
                <>
                  <div>
                    <AppIcon icon={arrowUpFromBracket} className="m-r-5" />
                    <AppIcon icon={angleDown} />
                  </div>
                  <span>{t.manage_user_toolbar_export}</span>
                </>
              )}
            </div>
          </div>
          <div className={`toolbar-group ${isOnlyIcons && 'expanded'}`}>
            <div className="toolbar-item m-0" onClick={handleDomainClick}>
              {isOnlyIcons ? (
                <AppTooltip
                  placement={'top'}
                  className="roles-selector"
                  title={t.manage_user_toolbar_domains}
                >
                  <AppIcon icon={globe} />
                </AppTooltip>
              ) : (
                <>
                  <AppIcon icon={globe} />
                  <span>{t.manage_user_toolbar_domains}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="toolbar-right">
          <div
            className={`expand-icon ${isOnlyIcons ? 'expanded' : ''}`}
            onClick={() => setIsOnlyIcons(!isOnlyIcons)}
          >
            <ExpandLessIcon fontSize="small" />
          </div>
        </div>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className="manage-user-export-menu">
          <MenuItem onClick={handleExportUserClick} disabled={isProvisioning}>
            {t.manage_user_toolbar_export_excel}
          </MenuItem>
          <MenuItem onClick={handleExportTemplateClick}>
            {t.manage_user_toolbar_export_template}
          </MenuItem>
          <MenuItem className="export-info-message">
            {t.manage_user_toolbar_export_info_message}
          </MenuItem>
        </div>
      </Popover>
      {isDeleteConfirm && (
        <DeleteUsersConfirmModal
          show={isDeleteConfirm}
          onClose={() => setIsDeleteConfirm(false)}
          onDelete={() => handleDeleteUser()}
        />
      )}
    </>
  );
};

export default GridHeaderToolbar;
